import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listOpportunities,
  getOpportunity,
  createOpportunity,
  updateOpportunity,
  deleteOpportunity,
  parseOpportunityUrl,
  researchOpportunity,
} from "@/api/opportunities";
import type { Opportunity, OpportunityInput } from "@sales-ops-amplify/shared";

const OPPORTUNITIES_QUERY_KEY = ["opportunities"] as const;

export function useOpportunities(params?: {
  company?: string;
  search?: string;
  q?: string;
  status?: string;
  stage?: string;
  stageId?: string;
  source?: string;
  pipelineId?: string;
  pipelineConfigId?: string;
  ownerUserId?: string;
  closeDateFrom?: string;
  closeDateTo?: string;
  includeArchived?: "true" | "false";
  sort?: "amount" | "closeDate" | "updatedAt";
  piped?: "true" | "false";
  limit?: number;
  offset?: number;
}) {
  // Dashboard callers omit paging and need the full set (drained server-side).
  // List UIs always pass limit/offset for server pagination.
  // Do not keep previous pages as placeholder — filtered list mutations
  // (stage change, search) would briefly show rows that no longer match.
  return useQuery({
    queryKey: [...OPPORTUNITIES_QUERY_KEY, params ?? {}],
    queryFn: () => listOpportunities(params),
    staleTime: 30_000,
  });
}

export function useOpportunity(opportunityId: string | undefined) {
  return useQuery({
    queryKey: [...OPPORTUNITIES_QUERY_KEY, "detail", opportunityId],
    queryFn: () => getOpportunity(opportunityId!),
    enabled: !!opportunityId,
    staleTime: 30_000,
  });
}

export function useCreateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OpportunityInput) => createOpportunity(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPPORTUNITIES_QUERY_KEY });
    },
  });
}

export function useUpdateOpportunity(opportunityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates: Partial<Opportunity>) => updateOpportunity(opportunityId, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPPORTUNITIES_QUERY_KEY });
    },
  });
}

export function useDeleteOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opportunityId: string) => deleteOpportunity(opportunityId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPPORTUNITIES_QUERY_KEY });
    },
  });
}

export function useParseOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ opportunityId, mode }: { opportunityId: string; mode?: "ai" | "offline" }) =>
      parseOpportunityUrl(opportunityId, mode ?? "ai"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPPORTUNITIES_QUERY_KEY });
    },
  });
}

export function useResearchOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ opportunityId, promptKind }: { opportunityId: string; promptKind?: string }) =>
      researchOpportunity(opportunityId, { promptKind }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPPORTUNITIES_QUERY_KEY });
    },
  });
}
